import { useCallback, useEffect, useMemo, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Container, Button, ProgressBar, Row, Col, Form, Alert, Modal, Image } from 'react-bootstrap';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import Sidebar from './Sidebar.js';
import icSuccess from './assets/img/ic-celebration-circle.svg';
import { useMediaQuery } from 'react-responsive';
import ProfileUpdateSuccessModal from './components/Profile/ProfileUpdateSuccessModal';

import OnboardingStepAbout from './components/UserOnboarding/OnboardingStepAbout';
import OnboardingStepAcademics from './components/UserOnboarding/OnboardingStepAcademics';
import OnboardingStepWorkPrefs from './components/UserOnboarding/OnboardingStepWorkPrefs';
import { trackEvent } from './services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from './services/analytics/analyticsEvents';
import {
    dismissIncompleteProfileReminderForSession,
    getInitialOnboardingStep,
    getOnboardingStepName,
} from './utils/onboarding';

export const getStepOnePayloadValues = (formData) => {
    const citizenships = (Array.isArray(formData.citizenships) && formData.citizenships.length > 0
        ? formData.citizenships
        : [formData.nationality]
    ).map(value => String(value || '').trim()).filter(Boolean).slice(0, 2);

    return {
        firstName: String(formData.firstName || '').trim(),
        familyName: String(formData.familyName || '').trim(),
        country: String(formData.country || '').trim(),
        city: String(formData.city || '').trim(),
        zipCity: String(formData.zipCity || '').trim(),
        about: String(formData.about || '').trim(),
        phone: String(formData.phone || '').trim(),
        citizenships,
    };
};

const EMPTY_OCCUPATION = { major: '', subMajor: '', minor: '', unit: '', skill: '' };

export const getWorkPreferencesValues = (user = {}, current = {}) => {
    const nested = user?.workPreferences && typeof user.workPreferences === 'object'
        ? user.workPreferences
        : {};
    const getValue = (field, fallback = '') => nested[field] ?? user?.[field] ?? current?.[field] ?? fallback;
    const getArray = (field) => {
        const value = getValue(field, []);
        return Array.isArray(value) ? value : [];
    };
    const occupations = getArray('occupations');

    return {
        ...current,
        countriesWorkPreferences: getArray('countriesWorkPreferences'),
        citiesWorkPreferences: getArray('citiesWorkPreferences'),
        languagesWorkPreferences: getArray('languagesWorkPreferences'),
        industryJobPreferences: getArray('industryJobPreferences'),
        employmentJobPreferences: getValue('employmentJobPreferences'),
        fteJobPreferences: getValue('fteJobPreferences'),
        jobTypeJobPreferences: getValue('jobTypeJobPreferences'),
        occupations: occupations.length
            ? occupations.slice(0, 3).map(occupation => ({ ...EMPTY_OCCUPATION, ...occupation }))
            : [{ ...EMPTY_OCCUPATION }],
        companyGlobalSize: getValue('companyGlobalSize'),
        companyLocalSize: getValue('companyLocalSize'),
        companyRating: getValue('companyRating'),
        fortune: getValue('fortune'),
    };
};

const UserOnboarding = () => {
    const navigate = useNavigate();
    const { user, setUser } = useContext(UserContext) || {};
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });

    const ONBOARDING_STARTED_SESSION_KEY = 'onboardingStartedTracked';
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const [companies, setCompanies] = useState([]);
    const [profileImage, setProfileImage] = useState(null);
    const [isImageDeleted, setIsImageDeleted] = useState(false);

    // One shared form state across steps (so you can go back without losing)
    const [formData, setFormData] = useState({
        profileImagePreview: '',

        // Step 1 (General + About + Residence)
        firstName: '',
        familyName: '',
        gender: '',
        email: '',
        birthDate: '',
        createdAt: '',
        phone: '',
        company: '',
        companyRole: '',
        nationality: '',
        citizenships: [],
        country: '',
        city: '',
        zipCity: '',

        about: '',

        // Step 2
        currentDegree: '',
        highestDegree: '',
        currentEducationalInstitution: '',
        academicsCountry: '',
        academicsCity: '',
        academicsStudyDirection: [],

        // Step 3
        workPreferences: getWorkPreferencesValues(),
    });

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await api.get('/api/companies');
                setCompanies(res.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };

        fetchCompanies();
    }, []);

    useEffect(() => {
        // If not logged in, don’t allow onboarding
        if (!user?._id) {
            navigate('/', { replace: true });
            return;
        }

        // Optional: prefill from user (safe + helps UX)
        setFormData(prev => ({
            ...prev,
            firstName: user?.firstName || prev.firstName,
            familyName: user?.familyName || prev.familyName,
            gender: user?.gender || prev.gender,
            email: user?.email || prev.email,
            birthDate: user?.birthDate || prev.birthDate,
            createdAt: user?.createdAt || prev.createdAt,
            phone: user?.phone || prev.phone,
            company: user?.company || prev.company,
            companyRole: user?.companyRole || prev.companyRole,

            nationality: user?.nationality || user?.citizenships?.[0] || prev.nationality,
            citizenships: Array.isArray(user?.citizenships) && user.citizenships.length > 0
                ? user.citizenships.slice(0, 2)
                : [user?.citizenship1 || user?.nationality || '', user?.citizenship2 || ''].filter(Boolean),
            country: user?.country || prev.country,
            city: user?.city || prev.city,
            zipCity: user?.zipCity || prev.zipCity,

            currentDegree: user?.currentDegree || user?.highestDegree || prev.currentDegree,
            highestDegree: user?.highestDegree || user?.currentDegree || prev.highestDegree,
            currentEducationalInstitution:
                user?.currentEducationalInstitution || prev.currentEducationalInstitution,
            academicsCountry: user?.academicsCountry || prev.academicsCountry,
            academicsCity: user?.academicsCity || prev.academicsCity,
            academicsStudyDirection: Array.isArray(user?.academicsStudyDirection)
                ? user.academicsStudyDirection
                : prev.academicsStudyDirection,

            workPreferences: getWorkPreferencesValues(user, prev.workPreferences),
            about: user?.about || prev.about,
            profileImagePreview: user?.profileImage || prev.profileImagePreview,
        }));

        if (!onboardingCompletedRef.current) {
            setStep(getInitialOnboardingStep(user));
        }
    }, [user, navigate]);

    useEffect(() => {
        if (!user?._id) return;

        const alreadyTracked =
            sessionStorage.getItem(ONBOARDING_STARTED_SESSION_KEY) === 'true';

        if (alreadyTracked) return;

        trackEvent(ANALYTICS_EVENTS.ONBOARDING_STARTED, {
            role: user?.role || '',
            source: 'registration_flow',
            initialStep: step,
            initialStepName: getOnboardingStepName(step),
            registrationCompleted: Boolean(user?.registrationCompleted),
            profileCompleted: Boolean(user?.profileCompleted),
        });

        sessionStorage.setItem(ONBOARDING_STARTED_SESSION_KEY, 'true');

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id]);

    const totalSteps = 3;
    const onboardingCompletedRef = useRef(false);

    useEffect(() => {
        if (!user?._id) return;

        if (onboardingCompletedRef.current) return;

        trackEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_VIEWED, {
            step,
            stepName: getOnboardingStepName(step),
            stepLabel: `Step ${step} - ${getOnboardingStepName(step)}`,
            totalSteps,
            progressPercent: Math.round((step / totalSteps) * 100),
        });
    }, [step, user?._id]);

    const progress = useMemo(() => Math.round((step / totalSteps) * 100), [step]);

    const setFieldError = useCallback((field, message) => {
        setErrors(prev => {
            const next = { ...prev };
            if (message) next[field] = message;
            else delete next[field];
            return next;
        });
    }, []);

    const handleDeleteImage = () => {
        setProfileImage(null);
        setFormData(prev => ({
            ...prev,
            profileImagePreview: '',
        }));
        setIsImageDeleted(true);
    };

    const handleChange = (eOrPatch) => {
        // Supports both: handleChange({ target: { name, value, files } }) and handleChange({ name: value })
        if (eOrPatch?.target?.name) {
            const { name, value, files } = eOrPatch.target;

            if (name === 'profileImage' && files && files.length > 0) {
                const file = files[0];
                const fileUrl = URL.createObjectURL(file);

                setProfileImage(file);
                setIsImageDeleted(false);

                setFormData(prev => ({
                    ...prev,
                    profileImagePreview: fileUrl,
                }));
                return;
            }

            setFormData(prev => ({ ...prev, [name]: value }));
            setFieldError(name, null);
            setShowErrorMessage(false);
            return;
        }

        if (eOrPatch && typeof eOrPatch === 'object') {
            setFormData(prev => ({ ...prev, ...eOrPatch }));
            return;
        }
    };

    const buildPayloadForStep = (s) => {
        if (s === 1) {
            const hasImageChange = !!profileImage || isImageDeleted;
            const { citizenships, ...stepOneValues } = getStepOnePayloadValues(formData);

            if (hasImageChange) {
                const fd = new FormData();

                Object.entries(stepOneValues).forEach(([key, value]) => fd.append(key, value));
                citizenships.forEach(value => fd.append('citizenships', value));

                if (profileImage) {
                    fd.append('profileImage', profileImage);
                }

                if (isImageDeleted) {
                    fd.append('removeProfileImage', 'true');
                }

                return fd;
            }

            return { ...stepOneValues, citizenships };
        }

        if (s === 2) {
            return {
                currentDegree: String(formData.currentDegree || '').trim(),
                highestDegree: String(formData.highestDegree || formData.currentDegree || '').trim(),
                currentEducationalInstitution: String(formData.currentEducationalInstitution || '').trim(),
                academicsCountry: String(formData.academicsCountry || '').trim(),
                academicsCity: String(formData.academicsCity || '').trim(),
                academicsStudyDirection: Array.isArray(formData.academicsStudyDirection)
                    ? formData.academicsStudyDirection
                    : [],
            };
        }

        // step 3
        return {
            workPreferences: formData.workPreferences || {},
        };
    };

    const validateStep = (s) => {
        let ok = true;

        setErrors(prev => {
            const next = { ...prev };
            delete next.general;

            if (s === 1) {
                delete next.firstName;
                delete next.familyName;
                delete next.gender;
                delete next.citizenships;
                delete next.country;
                delete next.city;
            }

            if (s === 2) {
                delete next.currentDegree;
                delete next.highestDegree;
                delete next.currentEducationalInstitution;
                delete next.academicsCountry;
                delete next.academicsCity;
                delete next.academicsStudyDirection;
            }

            if (s === 3) {
                delete next.countriesWorkPreferences;
                delete next.citiesWorkPreferences;
                delete next.languagesWorkPreferences;
                delete next.occupationsList;
            }

            return next;
        });

        if (s === 1) {
            if (!String(formData.firstName || '').trim()) {
                setFieldError('firstName', 'First name is required.');
                ok = false;
            }
            if (!String(formData.familyName || '').trim()) {
                setFieldError('familyName', 'Family name is required.');
                ok = false;
            }
            if (!String(formData.gender || '').trim()) {
                setFieldError('gender', 'Gender is required.');
                ok = false;
            }
            if (!String(formData.citizenships?.[0] || formData.nationality || '').trim()) {
                setFieldError('citizenships', 'Citizenship #1 is required.');
                ok = false;
            }
            if (!String(formData.country || '').trim()) {
                setFieldError('country', 'Country is required.');
                ok = false;
            }
            if (!String(formData.city || '').trim()) {
                setFieldError('city', 'City is required.');
                ok = false;
            }
        }

        if (s === 2) {
            if (!String(formData.currentDegree || '').trim()) {
                setFieldError('currentDegree', 'Current degree is required.');
                ok = false;
            }

            if (!String(formData.currentEducationalInstitution || '').trim()) {
                setFieldError('currentEducationalInstitution', 'Educational institution is required.');
                ok = false;
            }

            if (!String(formData.academicsCountry || '').trim()) {
                setFieldError('academicsCountry', 'Country is required.');
                ok = false;
            }

            if (!String(formData.academicsCity || '').trim()) {
                setFieldError('academicsCity', 'City is required.');
                ok = false;
            }

            const sdArr = Array.isArray(formData.academicsStudyDirection) ? formData.academicsStudyDirection : [];
            if (sdArr.map(v => String(v || '').trim()).filter(Boolean).length === 0) {
                setFieldError('academicsStudyDirection', 'Study direction is required.');
                ok = false;
            }
        }

        if (s === 3) {
            const wp = formData.workPreferences || {};

            const countriesArr = Array.isArray(wp.countriesWorkPreferences)
                ? wp.countriesWorkPreferences.map(v => String(v || '').trim()).filter(Boolean)
                : [];

            const citiesArr = Array.isArray(wp.citiesWorkPreferences)
                ? wp.citiesWorkPreferences.map(v => String(v || '').trim()).filter(Boolean)
                : [];

            const languagesArr = Array.isArray(wp.languagesWorkPreferences)
                ? wp.languagesWorkPreferences.map(v => String(v || '').trim()).filter(Boolean)
                : [];

            if (countriesArr.length === 0) {
                setFieldError('countriesWorkPreferences', 'At least one country is required.');
                ok = false;
            }

            if (citiesArr.length === 0) {
                setFieldError('citiesWorkPreferences', 'At least one city is required.');
                ok = false;
            }

            if (languagesArr.length === 0) {
                setFieldError('languagesWorkPreferences', 'At least one language is required.');
                ok = false;
            }

            const occupationRows = Array.isArray(wp.occupations) && wp.occupations.length
                ? wp.occupations
                : [{ ...EMPTY_OCCUPATION }];
            const occupationErrors = occupationRows.map(occupation => {
                const rowErrors = {};
                if (!String(occupation?.major || '').trim()) rowErrors.major = 'Major group is required.';
                if (!String(occupation?.subMajor || '').trim()) rowErrors.subMajor = 'Sub-major group is required.';
                if (!String(occupation?.minor || '').trim()) rowErrors.minor = 'Minor group is required.';
                if (!String(occupation?.skill || '').trim()) rowErrors.skill = 'Skill level is required.';
                return rowErrors;
            });

            if (occupationErrors.some(rowErrors => Object.keys(rowErrors).length > 0)) {
                setFieldError('occupationsList', occupationErrors);
                ok = false;
            }
        }

        return ok;
    };

    const saveStep = async (s) => {
        const ok = validateStep(s);
        if (!ok) {
            setShowErrorMessage(true);
            const requiredFieldsByStep = {
                1: ['firstName', 'familyName', 'gender', 'citizenship1', 'country', 'city'],
                2: ['currentDegree', 'currentEducationalInstitution', 'academicsCountry', 'academicsCity', 'academicsStudyDirection'],
                3: [
                    'countriesWorkPreferences',
                    'citiesWorkPreferences',
                    'languagesWorkPreferences',
                    ...((formData.workPreferences?.occupations?.length
                        ? formData.workPreferences.occupations
                        : [{ ...EMPTY_OCCUPATION }]).flatMap((_, index) => [
                        `majorGroup-${index}`,
                        `subMajorGroup-${index}`,
                        `minorGroup-${index}`,
                        `skillLevel-${index}`,
                    ])),
                ],
            };
            const isMissing = (controlId) => {
                if (controlId === 'citizenship1') {
                    return !String(formData.citizenships?.[0] || formData.nationality || '').trim();
                }

                if (controlId === 'academicsStudyDirection') {
                    return !Array.isArray(formData.academicsStudyDirection)
                        || formData.academicsStudyDirection.length === 0;
                }

                if (['countriesWorkPreferences', 'citiesWorkPreferences', 'languagesWorkPreferences'].includes(controlId)) {
                    return !Array.isArray(formData.workPreferences?.[controlId])
                        || formData.workPreferences[controlId].length === 0;
                }

                const occupationMatch = controlId.match(/^(majorGroup|subMajorGroup|minorGroup|skillLevel)-(\d+)$/);
                if (occupationMatch) {
                    const fieldByControl = {
                        majorGroup: 'major',
                        subMajorGroup: 'subMajor',
                        minorGroup: 'minor',
                        skillLevel: 'skill',
                    };
                    const occupation = formData.workPreferences?.occupations?.[Number(occupationMatch[2])];
                    return !String(occupation?.[fieldByControl[occupationMatch[1]]] || '').trim();
                }

                return !String(formData[controlId] || '').trim();
            };
            const firstInvalidControlId = requiredFieldsByStep[s]?.find(isMissing);

            window.setTimeout(() => {
                const field = firstInvalidControlId && document.getElementById(firstInvalidControlId);
                if (field) field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                else window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 0);
            return false;
        }

        setSaving(true);
        try {
            const payload = buildPayloadForStep(s);

            console.log('STEP', s, 'PAYLOAD:', payload);
            console.log('STEP', s, 'FORM DATA STATE:', formData);

            const res = payload instanceof FormData
                ? await api.patch('/api/users/me', payload, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                : await api.patch('/api/users/me', payload);

            localStorage.setItem('user', JSON.stringify(res.data.user));
            if (typeof setUser === 'function') setUser(res.data.user);

            setProfileImage(null);
            setIsImageDeleted(false);
            setShowErrorMessage(false);
            return true;
        } catch (err) {
            const msg = err?.response?.data?.msg || err?.response?.data?.message || 'Could not save. Please try again.';
            setFieldError('general', msg);
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleNext = async () => {
        const currentStep = step;
        const saved = await saveStep(currentStep);
        if (!saved) return;

        trackEvent(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, {
            step: currentStep,
            stepName: getOnboardingStepName(currentStep),
            stepLabel: `Step ${currentStep} - ${getOnboardingStepName(currentStep)}`,
            totalSteps,
            progressPercent: Math.round((currentStep / totalSteps) * 100),
        });

        if (currentStep < totalSteps) {
            setStep(prev => prev + 1);
        } else {
            onboardingCompletedRef.current = true;
            trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, {
                totalSteps,
                completionSource: 'student_onboarding',
            });

            setShowSuccessModal(true);
        }
    };

    const handleBack = () => {
        if (step === 1) {
            dismissIncompleteProfileReminderForSession();
            navigate('/discovery/companies', { replace: true });
            return;
        }
        setStep(prev => prev - 1);
    };

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1 onboarding-page">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && !isMobile && (
                                <Alert
                                    variant="danger"
                                    onClose={() => setShowErrorMessage(false)}
                                    dismissible
                                >
                                    <p className="mb-0">
                                        <strong>Error</strong> - There are items that require your attention
                                    </p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        {/* Main Content */}
                        <Col xl={12} className="py-5 onboarding-card-col">
                            <Card className="p-4 onboarding-card">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h3 className="mb-0">Complete your profile</h3>
                                    <div className="text-muted">Step {step} of {totalSteps}</div>
                                </div>

                                <ProgressBar now={progress} className="mb-3" />


                                {!!errors?.general && (
                                    <div className="alert alert-danger">{errors.general}</div>
                                )}

                                {step === 1 ? (
                                    <Form
                                        noValidate
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handleNext();
                                        }}
                                    >
                                        <OnboardingStepAbout
                                            formData={formData}
                                            handleChange={handleChange}
                                            handleDeleteImage={handleDeleteImage}
                                            profileImage={profileImage || formData.profileImagePreview || user?.profileImage}
                                            errors={errors}
                                            companies={companies}
                                            createdDate={user?.createdAt}
                                        />

                                        <div className="d-flex justify-content-between mt-4 onboarding-actions">
                                            <Button variant="outline-primary" onClick={handleBack} disabled={saving} type="button">
                                                Cancel
                                            </Button>

                                            <Button variant="primary" disabled={saving} type="submit">
                                                Next
                                            </Button>
                                        </div>
                                    </Form>
                                ) : step === 2 ? (
                                    <Form
                                        noValidate
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handleNext();
                                        }}
                                    >
                                        <OnboardingStepAcademics
                                            formData={formData}
                                            setFormData={setFormData}
                                            handleChange={handleChange}
                                            errors={errors}
                                        />

                                        <div className="d-flex justify-content-between mt-4 onboarding-actions">
                                            <div className="d-flex gap-2 onboarding-actions__secondary">
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={handleBack}
                                                    disabled={saving}
                                                    type="button"
                                                    className="d-none d-md-inline-flex"
                                                >
                                                    Back
                                                </Button>

                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => {
                                                        dismissIncompleteProfileReminderForSession();

                                                        trackEvent(ANALYTICS_EVENTS.ONBOARDING_MAYBE_LATER_CLICKED, {
                                                            step,
                                                            stepName: getOnboardingStepName(step),
                                                            destination: '/discovery/companies',
                                                            source: 'onboarding_cancel_button',
                                                        });

                                                        navigate('/discovery/companies', { replace: true });
                                                    }}
                                                    disabled={saving}
                                                    type="button"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>

                                            <Button variant="primary" disabled={saving} type="submit">
                                                Next
                                            </Button>
                                        </div>
                                    </Form>
                                ) : (
                                    <Form
                                        noValidate
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handleNext();
                                        }}
                                    >
                                        <OnboardingStepWorkPrefs
                                            formData={formData}
                                            setFormData={setFormData}
                                            handleChange={handleChange}
                                            errors={errors}
                                            setFieldError={setFieldError}
                                        />

                                        <div className="d-flex justify-content-between mt-4 onboarding-actions">
                                            <div className="d-flex gap-2 onboarding-actions__secondary">
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={handleBack}
                                                    disabled={saving}
                                                    type="button"
                                                    className="d-none d-md-inline-flex"
                                                >
                                                    Back
                                                </Button>

                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={() => {
                                                        dismissIncompleteProfileReminderForSession();

                                                        trackEvent(ANALYTICS_EVENTS.ONBOARDING_MAYBE_LATER_CLICKED, {
                                                            step,
                                                            stepName: getOnboardingStepName(step),
                                                            destination: '/discovery/companies',
                                                            source: 'onboarding_cancel_button',
                                                        });

                                                        navigate('/discovery/companies', { replace: true });
                                                    }}
                                                    disabled={saving}
                                                    type="button"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>

                                            <Button variant="primary" disabled={saving} type="submit">
                                                {step === totalSteps ? 'Finish' : 'Next'}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            {/* Success Modal */}
            {isMobile ? (
                <ProfileUpdateSuccessModal
                    show={showSuccessModal}
                    onHide={() => navigate('/discovery/companies', { replace: true })}
                    title="Profile completed"
                    message="Your onboarding information has been saved successfully."
                    icon={icSuccess}
                    actionLabel="Discover aBROADIFY"
                    onAction={() => {
                        setShowSuccessModal(false);
                        navigate('/discovery/companies', { replace: true });
                    }}
                />
            ) : (
                <Modal show={showSuccessModal} onHide={() => navigate('/discovery/companies', { replace: true })} centered>
                    <Modal.Header closeButton>
                        <Modal.Title></Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Image
                            src={icSuccess}
                            alt=""
                            className="d-block m-auto mb-4"
                        />
                        <h2 className="text-center mb-3">Profile completed</h2>
                        <p className="text-center">
                            Your onboarding information has been saved successfully.
                        </p>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-center">
                        <Button
                            variant="outline-primary"
                            onClick={() => {
                                setShowSuccessModal(false);
                                navigate('/discovery/companies', { replace: true });
                            }}
                        >
                            Discover aBROADIFY
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

        </>
    );
};

export default UserOnboarding;
